import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CaretDown, MagnifyingGlass, Plus, Check, WarningCircle } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/auth/AuthContext';
import { registrationApi, unregisteredLocationLabel } from './registrationApi';
import { useT } from '@/i18n/LanguageContext';

/**
 * <LocationPicker> — 設置場所 / 保管場所 / 現在地 を **一つの場所マスタ** から選ぶ。
 *
 * 仕様 2.1 / 2.2:
 *   • 木（パス）表示 + 絞り込み。既定は自分の部門、「すべて表示」で全件。
 *   • マスタに無い自由入力は消さずに「未登録の場所: <文字列>」として見せる。
 *   • 管理者はその場で場所を追加できる。
 */
export const LocationPicker = ({
  value = '', legacyText = '', onChange, departmentId = null, deptCode = null,
  preferDeptCode = null,
  testId = 'location-picker', placeholder = null, disabled = false,
}) => {
  const { t } = useT();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(!departmentId);
  const [adding, setAdding] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    registrationApi.locations({ deptCode }).then(setRows).catch(() => setRows([]));
  }, [deptCode]);
  useEffect(() => {
    if (!open) return;
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const selected = useMemo(() => rows.find((r) => r.id === value) || null, [rows, value]);
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const hit = rows.filter((r) => {
      if (!showAll && departmentId && r.department_id && r.department_id !== departmentId) return false;
      if (!needle) return true;
      // 2026-10-23 · パスの一部でも当たるようにする（「センスビー」でも「本社」でも）
      return `${r.name || ''} ${r.code || ''} ${r.path || ''} ${r.dept_code || ''} ${(r.aliases || []).join(' ')}`
        .toLowerCase().includes(needle);
    });
    // 選んだ部署の場所を先に出す（部署 HTR → HTR の場所が上）
    const want = String(preferDeptCode || '').toUpperCase();
    if (!want) return hit.slice(0, 200);
    const mine = hit.filter((r) => String(r.dept_code || '').toUpperCase() === want);
    const rest = hit.filter((r) => String(r.dept_code || '').toUpperCase() !== want);
    return [...mine, ...rest].slice(0, 200);
  }, [rows, q, showAll, departmentId, preferDeptCode]);

  const addHere = async () => {
    const name = q.trim();
    if (!name) return;
    setAdding(true);
    try {
      const created = await registrationApi.createLocation({
        name, location_type: 'shelf', code: name, dept_code: deptCode || 'COMMON' });
      registrationApi.invalidateLocations();
      const fresh = await registrationApi.locations({ force: true, deptCode });
      setRows(fresh);
      onChange?.(created.id, created);
      setOpen(false); setQ('');
      toast.success(`場所「${name}」を追加しました`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '場所の追加に失敗しました');
    } finally { setAdding(false); }
  };

  const label = selected ? (selected.path || selected.picker_label || selected.name)
    : (legacyText ? t('reg.location.unregistered', { text: legacyText }) : '');

  return (
    <div className="relative" ref={boxRef} data-testid={testId}>
      <button type="button" disabled={disabled} onClick={() => setOpen((v) => !v)}
        title={label || undefined}
        className="w-full min-h-10 mt-1 flex items-start justify-between gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-left outline-none hover:border-zinc-300 focus:border-teal-400 disabled:bg-zinc-50"
        data-testid={`${testId}-trigger`}>
        <span className={`break-words leading-snug ${label ? (selected ? 'text-zinc-800' : 'text-amber-700') : 'text-zinc-400'}`}>
          {selected?.dept_code && (
            <span className="mr-1 rounded border border-zinc-200 bg-zinc-50 px-1 text-[10px] font-semibold text-zinc-500">
              {selected.dept_code}
            </span>
          )}
          {label || placeholder || t('reg.location.ph')}
        </span>
        <CaretDown size={13} className="mt-1 shrink-0 text-zinc-400" />
      </button>
      {!selected && legacyText && (
        <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-700" data-testid={`${testId}-legacy-note`}>
          <WarningCircle size={12} />
          {t('reg.location.legacyNote')}
        </p>
      )}
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[26rem] max-w-[90vw] rounded-md border border-zinc-200 bg-white shadow-lg"
          data-testid={`${testId}-menu`}>
          <div className="flex items-center gap-1.5 border-b border-zinc-100 px-2 py-1.5">
            <MagnifyingGlass size={13} className="text-zinc-400" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t('reg.location.search')}
              className="h-8 border-0 px-0 text-sm shadow-none focus-visible:ring-0"
              data-testid={`${testId}-search`} />
          </div>
          {departmentId && (
            <label className="flex items-center gap-1.5 border-b border-zinc-100 px-3 py-1.5 text-[11px] text-zinc-500">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)}
                className="h-3.5 w-3.5 accent-teal-600" data-testid={`${testId}-show-all`} />
              {t('reg.location.showAll')}
            </label>
          )}
          <ul className="max-h-60 overflow-y-auto py-1">
            <li>
              <button type="button" onClick={() => { onChange?.('', null); setOpen(false); }}
                className="w-full px-3 py-1.5 text-left text-sm text-zinc-400 hover:bg-zinc-50"
                data-testid={`${testId}-clear`}>{t('reg.notSet')}</button>
            </li>
            {visible.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => { onChange?.(r.id, r); setOpen(false); setQ(''); }}
                  title={r.path || r.name}
                  className="w-full flex items-start gap-2 px-3 py-1.5 text-left text-sm hover:bg-zinc-50"
                  data-testid={`${testId}-option-${r.id}`}>
                  {r.id === value ? <Check size={13} className="mt-1 text-teal-600 shrink-0" />
                    : <span className="mt-1 w-[13px] shrink-0" />}
                  <span className="min-w-0 flex-1 break-words leading-snug"
                    style={{ paddingLeft: `${(r.depth || 0) * 12}px` }}>
                    {r.dept_code && (
                      <span className="mr-1 rounded border border-zinc-200 bg-zinc-50 px-1 text-[10px] font-semibold text-zinc-500">
                        {r.dept_code}
                      </span>
                    )}
                    <span className="text-zinc-800">{r.path || r.name || r.code}</span>
                    {r.code && <span className="ml-1 text-[10px] text-zinc-400">（{r.code}）</span>}
                  </span>
                </button>
              </li>
            ))}
            {visible.length === 0 && (
              <li className="px-3 py-2 text-[11px] text-zinc-400" data-testid={`${testId}-empty`}>
                {t('reg.location.empty')}
              </li>
            )}
          </ul>
          {isAdmin && q.trim() && !visible.some((r) => (r.name || '') === q.trim()) && (
            <button type="button" onClick={addHere} disabled={adding}
              className="w-full flex items-center gap-1.5 border-t border-zinc-100 px-3 py-2 text-left text-[12px] font-semibold text-teal-700 hover:bg-teal-50"
              data-testid={`${testId}-create`}>
              <Plus size={12} weight="bold" />
              {adding ? t('reg.adding') : t('reg.location.add', { name: q.trim() })}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
